import { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { registerAndroidBackButton } from "@/lib/native-setup";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { FreeTierGateProvider } from "@/contexts/FreeTierGateContext";
import { RequireAuth } from "@/components/RequireAuth";
import { RouteLogger } from "@/components/RouteLogger";
import { AppLayout } from "@/components/AppLayout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { OfflineGate } from "@/components/OfflineGate";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Onboarding from "./pages/Onboarding";
import Welcome from "./pages/Welcome";
import TryOn from "./pages/TryOn";
import StyleOutfit from "./pages/StyleOutfit";
import Result from "./pages/Result";
import AuraScore from "./pages/AuraScore";
import AuraHome from "./pages/AuraHome";
import AuraView from "./pages/AuraView";
import History from "./pages/History";
import Profile from "./pages/Profile";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Refund from "./pages/Refund";
import About from "./pages/About";
import DeleteAccount from "./pages/DeleteAccount";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import NotFound from "./pages/NotFound";
import AdminCosts from "./pages/AdminCosts";
import AdminCloud from "./pages/AdminCloud";
import AdminSecurity from "./pages/AdminSecurity";
import AdminBroadcasts from "./pages/AdminBroadcasts";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminOverview from "./pages/admin/AdminOverview";
import AdminRevenue from "./pages/admin/AdminRevenue";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminFunnel from "./pages/admin/AdminFunnel";
import AdminEngagement from "./pages/admin/AdminEngagement";
import AdminStability from "./pages/admin/AdminStability";
import AdminRefunds from "./pages/admin/AdminRefunds";
import AdminMobile from "./pages/admin/AdminMobile";
import PhotoTips from "./pages/PhotoTips";
import Purchases from "./pages/Purchases";
import AuthDiagnostics from "./pages/AuthDiagnostics";
import OAuthCatchAll from "./components/OAuthCatchAll";
import ResetPassword from "./pages/ResetPassword";
import AuthConfirmed from "./pages/AuthConfirmed";
import { AdSenseScript } from "@/components/ads/AdSenseScript";

const queryClient = new QueryClient();

// Inner shell so we can use `useNavigate` (must be inside <BrowserRouter>).
const AppShell = () => {
  const navigate = useNavigate();
  useEffect(() => {
    // Wire Android hardware back button to React Router history.
    // No-op on web and iOS.
    return registerAndroidBackButton((delta) => navigate(delta));
  }, [navigate]);
  return null;
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter>
          <AppShell />
          <AuthProvider>
            <FreeTierGateProvider>
            <AdSenseScript />
            <Toaster />
            <Sonner />
            <RouteLogger />
            <ErrorBoundary>
              <OfflineGate>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/auth/confirmed" element={<AuthConfirmed />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                {/* OAuth callback rescue: any Supabase/Lovable redirect that
                    lands on these paths gets its tokens captured and is then
                    sent to /app or /auth instead of falling through to 404. */}
                <Route path="/~oauth" element={<OAuthCatchAll />} />
                <Route path="/auth/callback" element={<OAuthCatchAll />} />
                <Route path="/oauth/callback" element={<OAuthCatchAll />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/refund" element={<Refund />} />
                <Route path="/about" element={<About />} />
                <Route path="/delete-account" element={<DeleteAccount />} />
                <Route path="/checkout/success" element={<CheckoutSuccess />} />
                <Route
                  path="/welcome"
                  element={
                    <RequireAuth requireOnboarded={false}>
                      <Welcome />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/onboarding"
                  element={
                    <RequireAuth requireOnboarded={false} redirectIfOnboarded>
                      <Onboarding />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/app"
                  element={
                    <RequireAuth>
                      <AppLayout />
                    </RequireAuth>
                  }
                >
                  <Route index element={<TryOn />} />
                  <Route path="style" element={<StyleOutfit />} />
                  <Route path="aura" element={<AuraHome />} />
                  <Route path="aura/:id" element={<AuraView />} />
                  <Route path="result/:id" element={<Result />} />
                  <Route path="result/:id/aura" element={<AuraScore />} />
                  <Route path="history" element={<History />} />
                  <Route path="profile" element={<Profile />} />
                  <Route path="photo-tips" element={<PhotoTips />} />
                  <Route path="purchases" element={<Purchases />} />
                  <Route path="diagnostics" element={<AuthDiagnostics />} />
                </Route>
                <Route
                  path="/admin"
                  element={
                    <RequireAuth>
                      <AdminLayout />
                    </RequireAuth>
                  }
                >
                  <Route index element={<Navigate to="/admin/overview" replace />} />
                  <Route path="overview" element={<AdminOverview />} />
                  <Route path="revenue" element={<AdminRevenue />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="funnel" element={<AdminFunnel />} />
                  <Route path="engagement" element={<AdminEngagement />} />
                  <Route path="stability" element={<AdminStability />} />
                  <Route path="operations" element={<AdminCosts />} />
                  <Route path="costs" element={<Navigate to="/admin/operations" replace />} />
                  <Route path="refunds" element={<AdminRefunds />} />
                  <Route path="mobile" element={<AdminMobile />} />
                  <Route path="broadcasts" element={<AdminBroadcasts />} />
                  <Route path="security" element={<AdminSecurity />} />
                  <Route path="cloud" element={<AdminCloud />} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
              </OfflineGate>
            </ErrorBoundary>
            </FreeTierGateProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
